import type { FlightState, Vec3 } from '../types';
import {
  AIR_DENSITY, BALL_AREA, BALL_MASS, GRAVITY,
  PRECESSION_RATE,
  windSpeedAtHeight,
} from '../constants';
import { spinFactor, getCd, getCl, applyDescentDampening, descentDragBoost } from './AeroCoefficients';
import { spinDecayDerivative } from './SpinDecay';
import { rk4Step } from './Integrator';
import type { AeroCorrection } from './ResidualCorrection';
import { interpolateGroundContact } from './GroundContact';
import {
  subVec3, scaleVec3, magnitudeVec3, normalizeVec3, crossVec3,
} from './vec3Math';

/**
 * Standalone golf ball flight model.
 * Zero dependencies on Babylon.js or Rapier.
 *
 * Forces: gravity + aerodynamic drag + Magnus lift/curve
 * Integration: RK4 at fixed timestep
 * Spin: velocity-dependent decay integrated in ODE (7th state variable)
 * Precession: discrete gyroscopic precession of spin axis per-step
 */

/** Pack FlightState into a flat number array for the integrator.
 *  State vector: [x, y, z, vx, vy, vz, spinRpm]
 */
function packState(s: FlightState): number[] {
  return [
    s.position.x, s.position.y, s.position.z,
    s.velocity.x, s.velocity.y, s.velocity.z,
    s.spinRate,
  ];
}

/** Unpack flat array back to position/velocity/spin. */
function unpackState(arr: number[]): { position: Vec3; velocity: Vec3; spinRate: number } {
  return {
    position: { x: arr[0], y: arr[1], z: arr[2] },
    velocity: { x: arr[3], y: arr[4], z: arr[5] },
    spinRate: Math.max(0, arr[6]),
  };
}

/**
 * Create the derivative function for the ball flight ODE.
 * Captures spin axis, wind, and air density. Spin rate is now a state variable.
 *
 * State vector: [x, y, z, vx, vy, vz, spinRpm]
 * Returns: [dx/dt, dy/dt, dz/dt, dvx/dt, dvy/dt, dvz/dt, dSpin/dt]
 *
 * @param spinAxis - Unit vector held fixed during this integration step
 * @param baseWind - Wind vector at reference height (10m)
 * @param airDensity - Air density in kg/m³ (defaults to 1.225 for backward compat)
 * @param correction - Optional Cd/Cl multipliers from residual correction model
 */
export function createFlightDerivatives(
  spinAxis: Vec3,
  baseWind: Vec3,
  airDensity: number = AIR_DENSITY,
  correction?: AeroCorrection,
) {
  const cdMult = correction?.cdMult ?? 1.0;
  const clMult = correction?.clMult ?? 1.0;
  const ddMult = correction?.ddMult ?? 1.0;
  // Derivatives must be pure: RK4 evaluates trial states in no physical-time order.
  const currentSpinAxis = { ...spinAxis };

  return (_t: number, state: number[]): number[] => {
    const vx = state[3], vy = state[4], vz = state[5];
    const currentSpinRpm = Math.max(0, state[6]);
    const posY = state[1]; // ball height for wind gradient
    const v: Vec3 = { x: vx, y: vy, z: vz };

    // ── Wind gradient: scale wind by height using power-law profile ──
    const baseWindSpeed = magnitudeVec3(baseWind);
    let wind: Vec3;
    if (baseWindSpeed < 0.001) {
      wind = { x: 0, y: 0, z: 0 };
    } else {
      const windScale = windSpeedAtHeight(1.0, Math.max(posY, 0));
      wind = scaleVec3(baseWind, windScale);
    }

    // Airspeed = ball velocity - wind (height-adjusted)
    const vAir = subVec3(v, wind);
    const speedAir = magnitudeVec3(vAir);

    // Spin factor
    const S = spinFactor(currentSpinRpm, speedAir);

    // Aero coefficients with residual correction
    const CdBase = getCd(S) * cdMult;
    const ClBase = getCl(S) * clMult;

    // Apply descent drag boost (steepens landing for all clubs, esp. low-spin)
    const Cd = descentDragBoost(CdBase, vy, speedAir, ddMult);

    // Apply descent dampening to lift (spin-dependent Magnus reduction
    // during descent models precession + turbulent wake disruption)
    const Cl = applyDescentDampening(ClBase, S, vy, speedAir);

    // Common factor: (0.5 * rho * A * |v_air|) / m
    const qAm = (0.5 * airDensity * BALL_AREA * speedAir) / BALL_MASS;

    // Drag acceleration: opposes airspeed direction
    const aDrag = scaleVec3(vAir, -Cd * qAm);

    // Wind influences drag and Magnus through relative air velocity above.
    // No unvalidated torque mutation inside RK4 derivative evaluations.

    // Magnus acceleration: perpendicular to spin axis and velocity
    const vAirHat = normalizeVec3(vAir);
    const magnusDir = crossVec3(currentSpinAxis, vAirHat);
    const aMagnus = scaleVec3(magnusDir, Cl * qAm * speedAir);

    // ── Velocity-dependent spin decay ──
    const dSpinRpm = spinDecayDerivative(currentSpinRpm, speedAir, airDensity);

    // Total derivatives: 7 elements
    return [
      vx, vy, vz,                                      // dx/dt = v
      aDrag.x + aMagnus.x,                              // dvx/dt
      aDrag.y + aMagnus.y - GRAVITY,                     // dvy/dt (gravity)
      aDrag.z + aMagnus.z,                               // dvz/dt
      dSpinRpm,                                          // dSpin/dt (velocity-dependent decay)
    ];
  };
}

/**
 * Apply gyroscopic precession to the spin axis.
 *
 * Aerodynamic torque causes the spin axis to precess toward the velocity
 * direction. This reduces the lift-producing (perpendicular) component
 * of spin over time, naturally lowering apex height and steepening descent.
 *
 * The precession rate scales with:
 *   - Dynamic pressure (ρ × v²) — faster ball = more torque
 *   - Inverse spin rate — lower spin = faster precession (less angular momentum)
 *
 * @param spinAxis - Current spin axis (mutated in place)
 * @param velocity - Current ball velocity
 * @param spinRpm - Current spin rate in RPM
 * @param dt - Timestep
 * @param airDensity - Air density
 */
function applyPrecession(
  spinAxis: Vec3,
  velocity: Vec3,
  spinRpm: number,
  dt: number,
  airDensity: number,
): void {
  const speed = magnitudeVec3(velocity);
  if (speed < 1.0 || spinRpm < 100) return;

  const vHat = normalizeVec3(velocity);

  // Project spin axis onto velocity direction (parallel component)
  const dot = spinAxis.x * vHat.x + spinAxis.y * vHat.y + spinAxis.z * vHat.z;

  // Perpendicular component = spinAxis - dot * vHat
  const perpX = spinAxis.x - dot * vHat.x;
  const perpY = spinAxis.y - dot * vHat.y;
  const perpZ = spinAxis.z - dot * vHat.z;

  const perpMag = Math.sqrt(perpX * perpX + perpY * perpY + perpZ * perpZ);
  if (perpMag < 0.001) return; // axis already aligned with velocity

  // Precession rate: proportional to dynamic pressure, inversely to angular momentum
  // ω_precession ∝ (ρ × v² × A × R) / (I × ω_spin)
  // Simplified: rate = PRECESSION_RATE × (ρ/ρ_ref) × (v/v_ref)² / (spinRpm/spinRpm_ref)
  //
  // Spin-dependent scaling: S / (S + S_REF) ensures low-spin shots (driver S≈0.08)
  // experience less precession than high-spin shots (irons S≈0.20-0.35).
  // Without this, driver loses ~19% lift (too much) while 7-iron only loses ~4%.
  const omegaRad = spinRpm * (2 * Math.PI / 60);
  const S = (omegaRad * 0.02135) / speed; // inline spin factor calc
  const spinScaling = S / (S + 0.12); // dampens precession for low-S (driver)
  const qNorm = airDensity * speed * speed; // proportional to dynamic pressure
  const precessionMag = PRECESSION_RATE * spinScaling * qNorm / (omegaRad * 60);

  // Decay the perpendicular component: move spin axis toward velocity
  const decay = Math.exp(-precessionMag * dt);
  spinAxis.x = dot * vHat.x + perpX * decay;
  spinAxis.y = dot * vHat.y + perpY * decay;
  spinAxis.z = dot * vHat.z + perpZ * decay;

  // Re-normalize
  const mag = Math.sqrt(spinAxis.x * spinAxis.x + spinAxis.y * spinAxis.y + spinAxis.z * spinAxis.z);
  if (mag > 0.001) {
    spinAxis.x /= mag;
    spinAxis.y /= mag;
    spinAxis.z /= mag;
  }
}

/**
 * Step the flight model forward by dt using RK4.
 * Returns a new FlightState with updated position, velocity, spin rate, and spin axis.
 *
 * @param state - Current flight state
 * @param dt - Timestep in seconds
 * @param wind - Base wind vector at reference height (10m)
 * @param airDensity - Air density in kg/m³ (defaults to 1.225)
 * @param correction - Optional Cd/Cl multipliers from residual correction model
 */
export function stepFlight(
  state: FlightState,
  dt: number,
  wind: Vec3,
  airDensity: number = AIR_DENSITY,
  correction?: AeroCorrection,
): FlightState {
  // Mutable spin axis for precession
  const spinAxis = { ...state.spinAxis };

  const derivFn = createFlightDerivatives(spinAxis, wind, airDensity, correction);
  const packed = packState(state);
  const nextPacked = rk4Step(state.time, packed, dt, derivFn);
  const { position, velocity, spinRate } = unpackState(nextPacked);

  // Apply gyroscopic precession to spin axis (discrete step)
  applyPrecession(spinAxis, velocity, spinRate, dt, airDensity);

  return {
    position,
    velocity,
    spinRate,
    spinAxis,
    time: state.time + dt,
  };
}

/**
 * Run the full flight simulation until the ball returns to ground level.
 * Returns the array of FlightStates at each step.
 *
 * @param initialState - Launch state
 * @param dt - Timestep
 * @param wind - Base wind vector at reference height (10m)
 * @param maxTime - Maximum simulation time in seconds
 * @param airDensity - Air density in kg/m³ (defaults to 1.225)
 * @param correction - Optional Cd/Cl multipliers from residual correction model
 */
export function simulateFullFlight(
  initialState: FlightState,
  dt: number,
  wind: Vec3,
  maxTime: number = 15,
  airDensity: number = AIR_DENSITY,
  correction?: AeroCorrection,
): FlightState[] {
  const frames: FlightState[] = [initialState];
  let current = initialState;

  while (current.time < maxTime) {
    const next = stepFlight(current, dt, wind, airDensity, correction);

    const groundState = interpolateGroundContact(current, next);
    if (groundState) {
      frames.push(groundState);
      break;
    }

    frames.push(next);
    current = next;
  }

  return frames;
}
